import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RestaurantTransaction, TransactionType } from '../../types/restaurants';
import { restaurantsService } from '../../services/restaurantsService';
import CreateRestaurantTransactionModal from '../modals/CreateRestaurantTransactionModal';
import ViewAllTransactionsModal from '../modals/ViewAllTransactionsModal';
import '../pages/Page.css';

interface RestaurantTransactionsProps {
  restaurantId: number;
  restaurantName: string;
  onTransactionCreated?: () => void;
}

const RestaurantTransactions: React.FC<RestaurantTransactionsProps> = ({
  restaurantId,
  restaurantName,
  onTransactionCreated
}) => {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<RestaurantTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewAllModalOpen, setIsViewAllModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadTransactions();
  }, [restaurantId]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const response = await restaurantsService.getRestaurantTransactions(restaurantId, 0, 5);
      if (response.success && response.data) {
        setTransactions(response.data.transactions);
      }
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTransaction = async (
    transactionType: TransactionType,
    amount: number,
    description: string
  ) => {
    const response = await restaurantsService.createRestaurantTransaction({
      restaurantId,
      transactionType,
      amount,
      descriptionHtml: description
    });

    if (response.success) {
      setSuccessMessage(t('restaurantDetails.transactionCreated'));
      setTimeout(() => setSuccessMessage(''), 3000);
      loadTransactions();
      if (onTransactionCreated) {
        onTransactionCreated();
      }
    } else {
      throw new Error(response.error || 'Failed to create transaction');
    }
  };

  const handleDeleteTransaction = async (transactionId: number) => {
    if (!window.confirm(t('restaurantDetails.confirmDeleteTransaction'))) {
      return;
    }

    try {
      const response = await restaurantsService.deleteRestaurantTransaction(transactionId);
      if (response.success) {
        setSuccessMessage(t('restaurantDetails.transactionDeleted'));
        setTimeout(() => setSuccessMessage(''), 3000);
        loadTransactions();
        if (onTransactionCreated) {
          onTransactionCreated();
        }
      } else {
        throw new Error(response.error || 'Failed to delete transaction');
      }
    } catch (error) {
      console.error('Failed to delete transaction:', error);
      alert(t('restaurantDetails.deleteTransactionError'));
    }
  };

  const formatDate = (timestamp: number) => {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString('en-US') + ' UZS';
  };

  const getTransactionTypeStyle = (type: TransactionType) => {
    if (type === TransactionType.FILL) {
      return {
        color: '#10b981',
        backgroundColor: '#ecfdf5',
        border: '1px solid #10b981'
      };
    }
    return {
      color: '#ef4444',
      backgroundColor: '#fef2f2',
      border: '1px solid #ef4444'
    };
  };

  return (
    <>
      <div className="detail-card full-width" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3>{t('restaurantDetails.transactions')}</h3>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="action-button accept"
            style={{ backgroundColor: '#3b82f6' }}
          >
            {t('restaurantDetails.createTransaction')}
          </button>
        </div>

        {successMessage && (
          <div className="success-message" style={{ marginBottom: '16px' }}>
            {successMessage}
          </div>
        )}

        {loading ? (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
            {t('restaurantDetails.loadingTransactions')}
          </p>
        ) : transactions.length === 0 ? (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
            {t('restaurantDetails.noTransactions')}
          </p>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  style={{
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    backgroundColor: '#fafafa',
                    opacity: transaction.isDeleted ? 0.5 : 1,
                    textDecoration: transaction.isDeleted ? 'line-through' : 'none',
                    position: 'relative'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          padding: '3px 10px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                          ...getTransactionTypeStyle(transaction.transactionType)
                        }}
                      >
                        {transaction.transactionType === TransactionType.FILL
                          ? t('restaurantTransactions.fill')
                          : t('restaurantTransactions.withdraw')}
                      </span>
                      <span style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                        {formatAmount(transaction.amount)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {formatDate(transaction.createdAt)}
                      </span>
                      {!transaction.isDeleted && (
                        <button
                          onClick={() => handleDeleteTransaction(transaction.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ef4444',
                            fontSize: '18px',
                            lineHeight: '1'
                          }}
                          title={t('restaurantDetails.deleteTransaction')}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                  <div
                    style={{ fontSize: '13px', color: '#374151' }}
                    dangerouslySetInnerHTML={{ __html: transaction.descriptionHtml }}
                  />
                </div>
              ))}
            </div>

            <button
              onClick={() => setIsViewAllModalOpen(true)}
              style={{
                width: '100%',
                padding: '10px',
                marginTop: '12px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151'
              }}
            >
              {t('restaurantDetails.viewAllTransactions')}
            </button>
          </>
        )}
      </div>

      <CreateRestaurantTransactionModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateTransaction}
        restaurantName={restaurantName}
      />

      <ViewAllTransactionsModal
        isOpen={isViewAllModalOpen}
        onClose={() => setIsViewAllModalOpen(false)}
        restaurantId={restaurantId}
        restaurantName={restaurantName}
      />
    </>
  );
};

export default RestaurantTransactions;
